/**
 * ModeBanner — top-of-shell session mode strip.
 *
 * Cart Mode Rulebook (Bill 2026-05-07; memo
 * `project_cart_mode_rulebook_20260507.md`) requires every non-Production
 * session to carry a visible color-coded banner so officers can never
 * confuse a Sandbox/Pilot/Test session for Production.
 *
 *   Production  → returns null (no banner; >99% of sessions)
 *   UAT-Live    → blue   strip ("Pilot (Live)" — banker label)
 *   Test-Live   → purple strip ("Test (Live)")
 *   Sandbox     → amber  strip ("Sandbox")
 *
 * Banker-facing label override (Bill 2026-05-08): code constant
 * `UAT-Live` is rendered as "Pilot (Live)" — see rulebook §"Banker-facing
 * labels". Code/API/SQL keeps UAT-Live; only UI text changes.
 *
 * Token-only — no literal hex. Theme-aware (light / dark / contrast)
 * via the --pcb-* + --ink-* token overrides defined in tokens.css.
 *
 * Sticky positioning (sticky top:0 inside the shell grid's first row)
 * keeps the strip visible during page scroll inside .shell__main.
 */

import { useState, type CSSProperties } from 'react';
import { Info } from 'lucide-react';
import { modeIconUrl } from '@/icons/flat-library';

/**
 * Canonical cart-mode constants. Mirror of `CartModeResolver.Modes` in the
 * Function App (`apps/PCBJxChangeApi/Services/CartModeResolver.cs`) — keep
 * the four string literals in sync between front-end + back-end.
 */
// 'Test-Only' = TEST MODE (Pilot/Test, Bill 2026-10-07): live Core Director, test customers
// only. A session state, not a cart Mode value (carts keep their live Mode).
export type SessionMode = 'Production' | 'UAT-Live' | 'Test-Live' | 'Sandbox' | 'Test-Only';

export interface ModeBannerProps {
  /** Effective session mode. `'Production'` (or unknown) renders nothing. */
  mode: SessionMode | string | undefined | null;
}

interface ModeSpec {
  /** Banker-facing label per rulebook §"Banker-facing labels". */
  label: string;
  /** Single-sentence summary of mode behavior (banner body text). */
  message: string;
  /** Tooltip shown on the info icon — fuller explanation. */
  tooltip: string;
  /** Background fill (token reference). */
  bg: string;
  /** Border / accent color (token reference). */
  border: string;
  /** Text color on the banner background (token reference). */
  text: string;
  /** ARIA label root + data-attribute hook for tests. */
  testid: string;
}

const SPECS: Record<Exclude<SessionMode, 'Production'>, ModeSpec> = {
  'UAT-Live': {
    label: 'Pilot (Live)',
    message:
      'PILOT (LIVE) — Real Core Director writes; internal notifications prefixed [UAT].',
    tooltip:
      'Pilot (UAT-Live) mode. Real customer, real Core Director writes, real outbound to the customer. Internal officer/supervisor notifications are tagged [UAT] so pilot activity stands out in dashboards. Cart is filed under the active pilot rollout.',
    bg: 'var(--banner-info-bg)',
    border: 'var(--pcb-blue)',
    text: 'var(--pcb-blue-fg)',
    testid: 'mode-banner-uat-live',
  },
  'Test-Live': {
    label: 'Test (Live)',
    message:
      'TEST (LIVE) — Real Core Director writes against 666-prefix CIFs; outbound routed to your TestRecipientEmail.',
    tooltip:
      'Test-Live mode. SuperAdmin / QA only. Real Core Director writes are made against 666-prefix test CIFs (no real customer impact). All SignNow + email + SMS outbound routes to your TestRecipientEmail / TestRecipientPhone — never to a real customer.',
    bg: 'var(--pcb-purple-soft-bg)',
    border: 'var(--pcb-purple)',
    text: 'var(--pcb-purple-fg)',
    testid: 'mode-banner-test-live',
  },
  Sandbox: {
    // Bill 2026-10-07: Training mode is labelled distinctly from Test mode.
    label: 'Training mode',
    message:
      'Training mode — simulated; nothing reaches Core Director, LexisNexis or customers.',
    tooltip:
      'Training (Sandbox) mode. Every Core Director change is simulated (made-up 666 numbers); Core lookups are real but read-only. LexisNexis returns a sample result and is not billed. E-mails meant for a customer (signing links, one-time codes) go to your test inbox instead, marked [SANDBOX]. You cannot make changes on a live application while in this mode.',
    bg: 'var(--banner-warn-bg)',
    border: 'var(--pcb-amber-text)',
    text: 'var(--pcb-amber-text)',
    testid: 'mode-banner-sandbox',
  },
  'Test-Only': {
    label: 'Test mode',
    message:
      'Test mode — test customers only (last name Test, 666 tax ID).',
    tooltip:
      'Test mode. Real Core Director, but only test customers: last name Test with a 666 SSN/TIN/ATIN or CIF (a test customer with no tax ID gets a 000 CIF). Real customers are hidden from search and cannot be added, opened, changed, signed or killed. Test applications are cleaned up after testing.',
    bg: 'var(--pcb-purple-soft-bg)',
    border: 'var(--pcb-purple)',
    text: 'var(--pcb-purple-fg)',
    testid: 'mode-banner-test-only',
  },
};

function isNonProductionMode(mode: unknown): mode is keyof typeof SPECS {
  return mode === 'UAT-Live' || mode === 'Test-Live' || mode === 'Sandbox' || mode === 'Test-Only';
}

/**
 * Primitive: ModeBanner. Renders nothing in Production (the >99% case);
 * renders a thin sticky color-coded strip otherwise.
 */
export function ModeBanner({ mode }: ModeBannerProps): JSX.Element {
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  // Bill 2026-05-08 — CRITICAL: must render an empty placeholder in
  // Production rather than `null`. ShellLayout uses CSS Grid with explicit
  // grid-template-rows; children are auto-placed by ORDER. Returning null
  // removes the DOM node entirely, which shifts every subsequent child
  // (TopBar / ModuleRail / body / footer) up one grid row — TopBar lands
  // in the 0px banner row, body lands in the fixed modulerail slot, and
  // the entire shell collapses. The empty placeholder preserves the slot
  // at 0px height so Production layout is byte-identical.
  if (!isNonProductionMode(mode)) {
    return <div aria-hidden style={{ height: 0, overflow: 'hidden' }} />;
  }
  const spec = SPECS[mode];

  const wrapperStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--sp-5)',
    padding: 'var(--sp-3) var(--sp-7)',
    background: spec.bg,
    color: spec.text,
    borderBottom: `2px solid ${spec.border}`,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.2px',
    lineHeight: 1.4,
    minHeight: 26,
    boxShadow: 'var(--sh-1)',
  };

  const labelStyle: CSSProperties = {
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.6px',
    padding: '1px var(--sp-5)',
    border: `1px solid ${spec.border}`,
    borderRadius: 'var(--r-full)',
    fontSize: 10,
    flexShrink: 0,
  };

  const infoButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    border: 'none',
    background: 'transparent',
    color: spec.text,
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
    position: 'relative',
  };

  const tooltipStyle: CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    right: 0,
    width: 280,
    padding: 'var(--sp-6) var(--sp-7)',
    background: 'var(--surface-card-elev)',
    color: 'var(--ink-800)',
    border: '1px solid var(--surface-border)',
    borderRadius: 'var(--r-md)',
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1.45,
    letterSpacing: 0,
    boxShadow: 'var(--sh-3)',
    zIndex: 40,
    textTransform: 'none',
  };

  // Bill 2026-05-13 — flat-library icon per non-Production mode. Production
  // returns null per design spec (default mode, no visual emphasis).
  const iconUrl = modeIconUrl(mode);

  return (
    <div
      role="status" data-tour="mode-banner"
      aria-label={`Session mode: ${spec.label}`}
      data-testid={spec.testid}
      data-mode={mode}
      style={wrapperStyle}
    >
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          aria-hidden
          width={20}
          height={20}
          style={{ flexShrink: 0, display: 'block' }}
        />
      )}
      <span style={labelStyle}>{spec.label}</span>
      <span>{spec.message}</span>
      <button
        type="button"
        aria-label={`About ${spec.label} mode`}
        aria-expanded={tooltipOpen}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
        onClick={() => setTooltipOpen((v) => !v)}
        style={infoButtonStyle}
      >
        <Info size={14} aria-hidden />
        {tooltipOpen && (
          <span role="tooltip" style={tooltipStyle}>
            {spec.tooltip}
          </span>
        )}
      </button>
    </div>
  );
}
